import { t } from "@lingui/core/macro";
import graphql from "babel-plugin-relay/macro";
import { useAtomValue, useSetAtom } from "jotai";
import { Suspense, useEffect, useMemo } from "react";
import { PreloadedQuery, usePreloadedQuery, useQueryLoader } from "react-relay";

import { setPlayerRateOnRoundAtom } from "@/screens/BookingDetail/NewPatronBookingFlow/components/EditBooking/atoms/actions/setPlayerRateOnRound/setPlayerRateOnRoundAtom";
import { getRateFromRoundIdAtom } from "@/screens/BookingDetail/NewPatronBookingFlow/components/EditBooking/atoms/selectors/getRateFromRoundIdAtom/getRatefromRoundIdAtom";
import { getRoundFromIdAtom } from "@/screens/BookingDetail/NewPatronBookingFlow/components/EditBooking/atoms/selectors/getRoundFromIdAtom/getRoundFromIdAtom";
import { getStartSlotIdAtom } from "@/screens/BookingDetail/NewPatronBookingFlow/components/EditBooking/atoms/selectors/getStartSlotId/getStartSlotIdAtom";
import { DropdownSelect } from "@whoosh/design-system/src/components/controls";
import { InfoBanner } from "@whoosh/design-system/src/components/display/InfoBanner";
import { Loader } from "@whoosh/design-system/src/components/display/Loader";
import { InfoIcon } from "@whoosh/design-system/src/components/icons_v2/general";
import { Text, VStack } from "@whoosh/design-system/src/components/primitive";

import { GuestPlayerRateEditQuery } from "./__generated__/GuestPlayerRateEditQuery.graphql";

type Props = {
  roundId: string;
  membershipTypeId?: string;
  nonPlayingHost: boolean;
};

export const GuestPlayerRateEdit = ({
  roundId,
  membershipTypeId,
  nonPlayingHost,
}: Props) => {
  const [queryRef, loadQuery] = useQueryLoader<GuestPlayerRateEditQuery>(
    GuestPlayerRateEditQueryString
  );

  const startSlotId = useAtomValue(getStartSlotIdAtom);

  useEffect(() => {
    if (startSlotId && membershipTypeId !== "") {
      loadQuery(
        {
          timeSlotId: startSlotId ?? "",
          membershipTypeId,
          nonPlayingHost,
        },
        { fetchPolicy: "store-or-network" }
      );
    }
  }, [loadQuery, membershipTypeId, nonPlayingHost, startSlotId]);

  return (
    <Suspense fallback={<Loader />}>
      {queryRef ? (
        <GuestRateEditInner queryRef={queryRef} roundId={roundId} />
      ) : null}
    </Suspense>
  );
};

type GuestRateEditInnerProps = {
  queryRef: PreloadedQuery<GuestPlayerRateEditQuery>;
  roundId: string;
};

export const GuestRateEditInner = ({
  queryRef,
  roundId,
}: GuestRateEditInnerProps) => {
  const currentRound = useAtomValue(getRoundFromIdAtom(roundId));

  const currentRate = useAtomValue(getRateFromRoundIdAtom(roundId));

  const setPlayerRateOnRound = useSetAtom(setPlayerRateOnRoundAtom);

  const data = usePreloadedQuery(GuestPlayerRateEditQueryString, queryRef);

  const rates = useMemo(() => {
    return data.node?.playerRates || [];
  }, [data.node?.playerRates]);

  const rateOptions =
    rates?.flatMap((rate) =>
      rate ? [{ value: rate.rate.id, label: rate.rate.name }] : []
    ) || [];

  const ratesIncludeCurrentRate = rates.some(
    (r) => r?.rate?.id === currentRate?.id
  );

  useEffect(() => {
    if (
      (!currentRate && rates.length) ||
      (currentRate?.visibility !== "NONE" && !ratesIncludeCurrentRate)
    ) {
      const firstRate = rates[0]?.rate;
      if (firstRate) {
        setPlayerRateOnRound({
          roundId: currentRound?.localId || "",
          rate: { id: firstRate.id },
          isTargetingAll: false,
        });
      }
    }
  }, [
    currentRate,
    rates,
    ratesIncludeCurrentRate,
    setPlayerRateOnRound,
    currentRound?.localId,
  ]);

  const hasStaffSetRate =
    currentRate && currentRate?.visibility && currentRate.visibility === "NONE";

  return (
    <>
      <Text
        variant="mainSmallSemiBold"
        color="text-primary"
      >{t`Guest Rate`}</Text>
      {!hasStaffSetRate ? (
        <DropdownSelect
          options={rateOptions}
          value={currentRate?.id}
          onChange={(value) => {
            setPlayerRateOnRound({
              roundId: currentRound?.localId || "",
              rate: { id: value },
              isTargetingAll: false,
            });
          }}
        />
      ) : (
        <VStack gap={2}>
          <InfoBanner
            leadingIcon={InfoIcon}
            message={t`This rate was set by staff. To change the rate, please contact the club.`}
            colorTheme={"WARNING"}
          />
          <DropdownSelect
            value={currentRate?.id}
            options={[
              { value: currentRate?.id || "", label: currentRate?.name || "" },
            ]}
            isDisabled={true}
            onChange={() => {}}
          />
        </VStack>
      )}
    </>
  );
};

const GuestPlayerRateEditQueryString = graphql`
  query GuestPlayerRateEditQuery(
    $timeSlotId: ID!
    $membershipTypeId: ID
    $nonPlayingHost: Boolean!
  ) {
    node(id: $timeSlotId) {
      ... on Slot {
        playerRates(
          type: GUEST
          membershipTypeId: $membershipTypeId
          nonPlayingHost: $nonPlayingHost
          visibleToMembershipTypeId: $membershipTypeId
        ) {
          rate {
            id
            name
            code
            visibility
            appliesToMembershipType(membershipTypeId: $membershipTypeId)
          }
        }
      }
    }
  }
`;
